import { MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { Pill } from '../../../components/pill/Pill';
import { ITableHeader, Table, TableSortOrder } from '../../../components/table/Table';
import { getCreditNotes } from '../models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { decimateAndFormatNumber } from '../../../utils/number';
import { getInvoiceTotals } from '../../../domain/invoice-totals';
import { CreditNoteStatusToColor, CreditNoteStatusToText } from '../utils/status';
import { ExportCreditNotesButton } from './ExportCreditNotesButton';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { LinkButton } from '../../../components/button/ButtonLink';
import { PageHeading } from 'components/PageHeading';
import { ListPage } from 'components/page-templates/ListPage';

type CreditNoteSortBy = 'date' | 'expiresAt' | 'status' | 'creditNoteNumber';

const CREDIT_NOTES_HEADER: ITableHeader[] = [
  {
    id: 'creditNoteNumber',
    name: 'Creditnota Nummer',
    sortable: true,
  },
  {
    id: 'date',
    name: 'Datum',
    sortable: true,
  },
  {
    id: 'expiresAt',
    name: 'Vervaldatum',
    sortable: true,
  },
  {
    id: 'customer',
    name: 'Klant',
  },
  {
    id: 'status',
    name: 'Status',
    sortable: true,
  },
  {
    id: 'total',
    name: 'Totaal',
  },
];

const CreditNotesPage = () => {
  const [searchValue, setSearchValue] = useState('');
  const [sortBy, setSortBy] = useState<CreditNoteSortBy | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>(undefined);
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getCreditNotes(
        variables.cursor,
        variables.take,
        {
          search: searchValue,
        },
        sortBy,
        sortOrder,
      );
    },
    [searchValue, sortBy, sortOrder],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  useEffect(() => {
    page.reset();
  }, [searchValue, sortBy, sortOrder]);

  const title = `Creditnota's`;

  return (
    <>
      <PageHeader title={title} />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={
              <Breadcrumb
                parentItem={{
                  name: 'Financieel',
                  to: '/internal/finance',
                }}
                currentItem={title}
              />
            }
            rightSide={
              <>
                <ExportCreditNotesButton />
                <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                  Nieuwe Creditnota
                </LinkButton>
              </>
            }
          />
        }
        panel
        filters={
          <Input
            type="text"
            placeholder="Zoek een creditnota..."
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={<MagnifyingGlassIcon className="input-icon" />}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={CREDIT_NOTES_HEADER}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(id, order) => {
            setSortBy(id as CreditNoteSortBy | undefined);
            setSortOrder(order);
          }}
          mapData={(creditNote) => {
            const status = creditNote.status;
            const totals = getInvoiceTotals([
              ...creditNote.creditNoteOrders
                .map((o) =>
                  o.lines.map((l) => {
                    return {
                      ...l,
                      vatRateId: l.vatRate.id,
                    };
                  }),
                )
                .flat(),
              ...creditNote.manualLines.map((v) => {
                return {
                  ...v,
                  vatRateId: v.vatRate.id,
                };
              }),
            ]);

            return [
              <Link to={`${creditNote.id}`} className="link-text">
                {creditNote.creditNoteNumber ?? 'DRAFT'}
              </Link>,
              formatDate(creditNote.date),
              creditNote.expiresAt ? formatDate(creditNote.expiresAt) : '-',
              creditNote.customer.name,
              <Pill color={CreditNoteStatusToColor[status]} size="sm">
                {CreditNoteStatusToText[status]}
              </Pill>,
              `€ ${decimateAndFormatNumber(totals.totalInclVat, 2, {
                decimalSeperator: ',',
              })}`,
            ];
          }}
        />
      </ListPage>
    </>
  );
};

export default CreditNotesPage;
